// src/pages/dashboard/Settings/DeductionTypesManagement.js
import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  message,
  Popconfirm,
  Typography,
  Tooltip,
  Alert,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import { supabase } from '../../../services/supabase';
import deductionService from '../../../services/deductionService';
import { SectionHead, StatusPill } from '../../../components/ui';

const { Text } = Typography;
const { TextArea } = Input;

const DeductionTypesManagement = ({ companyId }) => {
  const [deductionTypes, setDeductionTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (companyId) {
      fetchDeductionTypes();
    }
  }, [companyId]);

  const fetchDeductionTypes = async () => {
    try {
      setLoading(true);
      const types = await deductionService.getDeductionTypes(companyId);
      setDeductionTypes(types);
    } catch (error) {
      message.error('Failed to fetch deduction types');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingType(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (type) => {
    setEditingType(type);
    form.setFieldsValue({
      name: type.name,
      description: type.description,
      is_percentage: type.is_percentage,
      amount: type.amount
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const deductionTypeData = {
        ...values,
        company_id: companyId
      };

      if (editingType) {
        await deductionService.updateDeductionType(editingType.id, values);
        message.success('Deduction type updated successfully');
      } else {
        await deductionService.createDeductionType(deductionTypeData);
        message.success('Deduction type created successfully');
      }

      setModalVisible(false);
      fetchDeductionTypes();
    } catch (error) {
      message.error(editingType ? 'Failed to update deduction type' : 'Failed to create deduction type');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (typeId) => {
    try {
      setLoading(true);
      await deductionService.deleteDeductionType(typeId);
      message.success('Deduction type deleted successfully');
      fetchDeductionTypes();
    } catch (error) {
      message.error('Failed to delete deduction type');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDefaults = async () => {
    try {
      setLoading(true);
      await deductionService.createDefaultDeductionTypes(companyId);
      message.success('Default deduction types created successfully');
      fetchDeductionTypes();
    } catch (error) {
      message.error('Failed to create default deduction types');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Text strong>{text}</Text>
          {record.is_percentage ? (
            <StatusPill tone="primary">Percentage</StatusPill>
          ) : (
            <StatusPill tone="neutral">Fixed amount</StatusPill>
          )}
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text type="secondary">{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Default Amount',
      key: 'amount',
      width: 150,
      render: (_, record) => {
        if (record.is_percentage) {
          return (
            <span className="num num--strong">
              {record.amount}%
            </span>
          );
        }
        return (
          <span className="num num--strong">
            Rs {record.amount?.toFixed(2) || '0.00'}
          </span>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size={2}>
          <Button
            type="text"
            className="icon-action"
            data-tone="primary"
            aria-label="Edit deduction type"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete this deduction type?"
            description="Existing employee deductions will not be affected."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              className="icon-action"
              data-tone="danger"
              aria-label="Delete deduction type"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <SectionHead
        title="Deduction types"
        subtitle="Deductions you can apply to an employee's pay, as a percentage of base pay or a fixed amount."
        actions={
          <>
            {deductionTypes.length === 0 && (
              <Button onClick={handleCreateDefaults} loading={loading}>
                Create Mauritius defaults (CSG, Levy, NPF)
              </Button>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNew}
            >
              Add deduction type
            </Button>
          </>
        }
      />

      {deductionTypes.length === 0 && (
        <Alert
          message="No Deduction Types Found"
          description="Create deduction types to start managing employee deductions. Click 'Create Mauritius Defaults' to add CSG, Levy, NPF automatically."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        columns={columns}
        dataSource={deductionTypes}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
      />

      <Modal
        title={editingType ? 'Edit Deduction Type' : 'Add New Deduction Type'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            is_percentage: false,
            amount: 0
          }}
        >
          <Form.Item
            name="name"
            label="Deduction Type Name"
            rules={[
              { required: true, message: 'Please enter deduction type name' },
              { min: 2, message: 'Name must be at least 2 characters' },
              { max: 100, message: 'Name cannot exceed 100 characters' }
            ]}
          >
            <Input placeholder="e.g., CSG, Levy, NPF, Advance Payment" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { max: 500, message: 'Description cannot exceed 500 characters' }
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Brief description of this deduction type..."
            />
          </Form.Item>

          <Form.Item
            name="is_percentage"
            label={
              <Space>
                Calculation Method
                <Tooltip title="Percentage deductions are calculated on base gross pay (before allowances). Fixed deductions are set amounts.">
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
            }
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Percentage"
              unCheckedChildren="Fixed Amount"
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.is_percentage !== currentValues.is_percentage
            }
          >
            {({ getFieldValue }) => {
              const isPercentage = getFieldValue('is_percentage');

              return (
                <Form.Item
                  name="amount"
                  label={isPercentage ? 'Default Percentage' : 'Default Amount'}
                  rules={[
                    { required: true, message: 'Please enter amount' },
                    { type: 'number', min: 0, message: 'Amount must be positive' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={isPercentage ? 100 : undefined}
                    step={isPercentage ? 0.1 : 0.01}
                    placeholder={isPercentage ? 'e.g., 4.0' : 'e.g., 500'}
                    addonAfter={isPercentage ? '%' : 'Rs'}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Form>

        <Alert
          message="Important Notes"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><strong>Percentage deductions</strong> are calculated on base gross pay (BEFORE allowances are added)</li>
              <li><strong>Fixed deductions</strong> are subtracted as-is from gross pay</li>
              <li>Example: Base pay Rs 20,000, CSG 4% = Rs 800 deduction</li>
              <li>The default amount can be overridden when assigning to employees</li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Modal>
    </Card>
  );
};

export default DeductionTypesManagement;